import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import apiUrl from '../config/apiUrl';
import { z, ZodError } from 'zod';
import { RequestError } from './RequestError';
import { RequestErrorType } from './RequestErrorType';
import { ErrorCode } from '@web/common';
import { ServerError } from './ServerError';
import { keycloakStore } from '../keycloakStore';
import { Routes } from '../route/Routes';
import { showFailedRequestNotification } from '../component/notification';
import * as Sentry from '@sentry/react';

type RequestOptions = {
  // false: the caller shows the error itself, e.g. next to the element that failed
  notifyOnError?: boolean;
};

class AuthenticatedAPI {
  private readonly client: AxiosInstance;

  constructor(requestTimeout: number = 10000) {
    this.client = axios.create({
      timeout: requestTimeout,
      headers: { 'Content-Type': 'application/json' },
      baseURL: apiUrl,
    });

    // Inject Sentry distributed tracing headers so frontend and backend
    // events are linked under the same trace in Sentry.
    this.client.interceptors.request.use((config) => {
      const traceData = Sentry.getTraceData();
      if (traceData['sentry-trace']) {
        config.headers.set('sentry-trace', traceData['sentry-trace']);
        if (traceData['baggage']) {
          config.headers.set('baggage', traceData['baggage']);
        }
      }
      return config;
    });
  }

  public async get<ReturnType, Input>(
    resourceUrl: string,
    schema: z.Schema<ReturnType, Input>,
    queryParams?: object,
    requestConfig?: AxiosRequestConfig,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.get<ReturnType>(resourceUrl, {
          params: queryParams,
          headers: {
            ...this.extractAuthorizationHeader(),
          },
          ...requestConfig,
        }),
      schema,
    );
  }

  public async post<Type, ReturnType, Input>(
    resourceUrl: string,
    data: Type,
    schema: z.Schema<ReturnType, Input>,
    config?: AxiosRequestConfig,
    options?: RequestOptions,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.post<ReturnType>(resourceUrl, data, {
          ...config,
          headers: {
            ...this.extractAuthorizationHeader(),
          },
        }),
      schema,
      options,
    );
  }

  public async patch<Type, ReturnType>(
    resourceUrl: string,
    data: Type,
    schema: z.Schema<ReturnType>,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.patch<ReturnType>(resourceUrl, data, {
          headers: {
            ...this.extractAuthorizationHeader(),
          },
        }),
      schema,
    );
  }

  public async put<Type, ReturnType>(
    resourceUrl: string,
    data: Type,
    schema: z.Schema<ReturnType>,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.put<ReturnType>(resourceUrl, data, {
          headers: {
            ...this.extractAuthorizationHeader(),
          },
        }),
      schema,
    );
  }

  public async noBodyPut<ReturnType>(
    resourceUrl: string,
    schema: z.Schema<ReturnType>,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.put<ReturnType>(resourceUrl, {
          headers: {
            ...this.extractAuthorizationHeader(),
          },
        }),
      schema,
    );
  }

  public async delete<ReturnType>(
    resourceUrl: string,
    schema: z.Schema<ReturnType>,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.delete<ReturnType>(resourceUrl, {
          headers: {
            ...this.extractAuthorizationHeader(),
          },
        }),
      schema,
    );
  }

  private async request<RequestData, ResponseData, Input>(
    block: () => Promise<AxiosResponse<unknown, RequestData>>,
    schema: z.Schema<ResponseData, Input>,
    options?: RequestOptions,
  ): Promise<ResponseData> {
    const response = await this.retryOnceFlow(block).catch((error) =>
      this.handleAPIError(error, options),
    );

    const parseResult = schema.safeParse(response.data);
    if (parseResult.success) {
      return parseResult.data;
    }

    throw this.handleParseError(parseResult.error, response.config);
  }

  private isStatusCode401(error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.status === 401;
    }
    return false;
  }

  private async retryOnceFlow<RequestData>(
    block: () => Promise<AxiosResponse<unknown, RequestData>>,
  ): Promise<AxiosResponse<unknown, RequestData>> {
    try {
      return await block();
    } catch (error: unknown) {
      if (this.isStatusCode401(error)) {
        // the first time we get a 401, we update the access token and retry the request
        await keycloakStore.updateToken();
        try {
          return await block();
        } catch (error: unknown) {
          if (this.isStatusCode401(error)) {
            // the second time we get a 401, we redirect the user to the login page
            keycloakStore.login();
            throw new RequestError(RequestErrorType.Unauthorized);
          } else {
            // if it is not a 401, we simply throw it
            throw error;
          }
        }
      } else {
        // if it is not a 401, we simply throw it
        throw error;
      }
    }
  }

  private handleAPIError = async (
    error: AxiosError,
    options?: RequestOptions,
  ): Promise<never> => {
    const apiResponseData = error?.response?.data as
      | undefined
      | { errorMessage?: string; error?: string; errorCode?: string };

    const statusCode = error.response?.status;
    const serverErrorCode = apiResponseData?.errorCode as undefined | ErrorCode;
    const serverTracingId = error.response?.headers['tracing-id'];

    console.error(
      JSON.stringify({
        statusCode: statusCode,
        error: error.message,
        tracingId: serverTracingId,
        apiResponse: apiResponseData,
      }),
    );
    Sentry.captureEvent({
      message: `[AuthenticatedAPI] ${error.config?.url}`,
      level: 'error',
      extra: {
        error,
        errorString: JSON.stringify(error),
      },
    });

    // if the user was deactivated, we redirect him to the deactivated page
    // currently, this prompts a reload of keycloak
    // this means, if you log into a user, who's deactivated, (you'll see the loading page of keycloak twice, once the normal one, once the deactivated one (reload))
    if (serverErrorCode === ErrorCode.UserDeactivated) {
      window.location.href = `${Routes.Study}`;
    }

    if (serverErrorCode) {
      // we do not want to show a notification for these errors, since they should be handled on the calling site!
      // we might want to consider the same for sentry events here
      throw new ServerError(serverErrorCode);
    } else {
      if (options?.notifyOnError !== false) {
        showFailedRequestNotification(
          RequestErrorType.Generic,
          serverTracingId,
        );
      }
      throw new RequestError(RequestErrorType.Generic);
    }
  };

  private handleParseError<T>(error: ZodError<T>, config: AxiosRequestConfig) {
    console.error(error);

    Sentry.captureEvent({
      message: `[AuthenticatedAPI] Error Parsing: ${error.name} `,
      level: 'error',
      extra: { error },
    });

    throw new RequestError(
      RequestErrorType.ParseIssue,
      JSON.stringify({
        error: error,
        config,
      }),
    );
  }

  private extractAuthorizationHeader = () => {
    const token = keycloakStore.token;
    return {
      Authorization: `bearer ${token}`,
    };
  };
}

export const authenticatedAPI = new AuthenticatedAPI();
